import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MessageCircle, X, Send, ShoppingCart, Clock, Gift, Globe, ExternalLink, User, Mail, Phone, Building2, ChevronDown, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const SITIOSYA_URL = process.env.REACT_APP_SITIOSYA_URL || "https://sitiosya.com.uy";

// ─── Helpers ──────────────────────────────────────────────────────────────────
function parseIso(str) {
  if (!str) return null;
  try { return new Date(str); } catch { return null; }
}

function fmtCountdown(ms) {
  if (ms <= 0) return { h: "00", m: "00", s: "00", expired: true };
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return { h: String(h).padStart(2, "0"), m: String(m).padStart(2, "0"), s: String(s).padStart(2, "0"), expired: false };
}

function formatPrice(p, c) {
  if (c === "UYU") return `$U ${Math.round(p).toLocaleString("es-UY")}/mes`;
  if (c === "EUR") return `€${p}/mes`;
  const amount = p === Math.floor(p) ? String(Math.floor(p)) : p.toFixed(2);
  return `USD $${amount}/mes`;
}

// ─── Tier hook ────────────────────────────────────────────────────────────────
function useTier(siteInfo) {
  const [state, setState] = useState(null);
  useEffect(() => {
    if (!siteInfo) return;
    const basePrice = siteInfo.offer_price || 20;
    const currency  = siteInfo.offer_currency || "USD";
    const t1 = parseIso(siteInfo.tier_1_expires_at);
    const t2 = parseIso(siteInfo.offer_expires_at);
    const compute = () => {
      const now = Date.now();
      if (t1 && now < t1.getTime()) {
        const price = Math.round(basePrice * 0.5 * 100) / 100;
        setState({ level: 1, price, fullPrice: basePrice, currency, countdownMs: t1.getTime() - now, countdown: fmtCountdown(t1.getTime() - now) });
      } else if (t2 && now < t2.getTime()) {
        setState({ level: 2, price: basePrice, fullPrice: null, currency, countdownMs: t2.getTime() - now, countdown: fmtCountdown(t2.getTime() - now) });
      } else {
        setState({ level: 3, price: basePrice * 2, fullPrice: null, currency, countdownMs: 0, countdown: fmtCountdown(0) });
      }
    };
    compute();
    const id = setInterval(compute, 1000);
    return () => clearInterval(id);
  }, [siteInfo]);
  return state;
}

// ─── Cycling WA label — alterna entre frases persuasivas y "¿Consultas?" ─────
const WA_LABELS_TIER1 = [
  "¿Consultas?", "¡50% OFF hoy!", "¿Consultas?", "¿Lo activamos?",
  "¿Consultas?", "Oferta única vez", "¿Consultas?", "¿Tenés dudas?",
];
const WA_LABELS_BASE = [
  "¿Consultas?", "¡Tu sitio está listo!", "¿Consultas?", "¿Lo activamos hoy?",
  "¿Consultas?", "Sin compromiso", "¿Consultas?", "¿Tenés dudas?",
];

function useWaLabel(tier) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const start = setTimeout(() => {
      const id = setInterval(() => setIdx(i => i + 1), 3500);
      return () => clearInterval(id);
    }, 10000);
    return () => clearTimeout(start);
  }, []);
  const labels = tier?.level === 1 ? WA_LABELS_TIER1 : WA_LABELS_BASE;
  return labels[idx % labels.length];
}

// ─── Exit Intent Popup ────────────────────────────────────────────────────────
function ExitIntentModal({ onBuy, onClose }) {
  const [visible, setVisible] = useState(false);
  const [seconds, setSeconds] = useState(180); // 3 minutos
  useEffect(() => {
    setTimeout(() => setVisible(true), 50);
    const id = setInterval(() => setSeconds(s => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, []);
  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");
  // Verde → amarillo oscuro al llegar a 2:00 → rojo al llegar a 1:00
  const timerColor  = seconds > 120 ? "#16a34a" : seconds > 60 ? "#b45309" : "#dc2626";
  const timerBg     = seconds > 120 ? "rgba(22,163,74,0.08)" : seconds > 60 ? "rgba(180,83,9,0.08)" : "rgba(220,38,38,0.08)";
  const timerBorder = seconds > 120 ? "rgba(22,163,74,0.25)" : seconds > 60 ? "rgba(180,83,9,0.25)" : "rgba(220,38,38,0.25)";
  const close = () => { setVisible(false); setTimeout(onClose, 300); };
  const buy   = () => { setVisible(false); setTimeout(onBuy, 200); };
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4" style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)" }}>
      <div
        data-testid="exit-intent-modal"
        className="bg-white rounded-3xl w-full max-w-sm overflow-hidden shadow-2xl"
        style={{ transform: visible ? "scale(1) translateY(0)" : "scale(0.9) translateY(20px)", opacity: visible ? 1 : 0, transition: "all 0.3s cubic-bezier(0.34,1.56,0.64,1)" }}
      >
        <div className="bg-gradient-to-r from-[#0A0A0A] to-[#1a1a1a] px-5 pt-5 pb-4 relative">
          <button onClick={close} className="absolute top-4 right-4 text-gray-500 hover:text-white transition-colors"><X size={18} /></button>
          <div className="bg-[#FF4F00] text-white text-[10px] font-black px-2.5 py-1 rounded-full tracking-wide inline-block mb-3 animate-pulse">OFERTA EXCLUSIVA</div>
          <h2 className="text-xl font-black text-white leading-tight">¡Espera! Llevate tu dominio gratis 🎁</h2>
          <p className="text-sm text-gray-400 mt-1">Si activás tu sitio <span className="text-[#ffc700] font-bold">ahora mismo</span>, te incluimos un dominio gratis por 1 año completo.</p>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div className="bg-gradient-to-r from-[#059669]/10 to-[#0074b7]/10 border border-[#059669]/20 rounded-2xl p-3.5 flex items-center gap-3">
            <div className="w-10 h-10 bg-[#059669] rounded-xl flex items-center justify-center flex-shrink-0"><Globe size={20} className="text-white" /></div>
            <div>
              <p className="text-sm font-black text-gray-900">Dominio gratis por 1 año</p>
              <p className="text-xs text-gray-500">.com · .com.uy · .uy · .net — a elección</p>
              <p className="text-xs font-bold text-[#059669]">Valorado en USD 30 — GRATIS para vos</p>
            </div>
          </div>
          {/* Countdown con colores dinámicos */}
          <div className="rounded-xl py-2.5 px-3 flex items-center justify-between" style={{ background: timerBg, border: `1px solid ${timerBorder}`, transition: "background 0.7s, border-color 0.7s" }}>
            <div className="flex items-center gap-2">
              <Clock size={14} style={{ color: timerColor }} />
              <span className="text-xs text-gray-700 font-medium">Esta oferta vence en:</span>
            </div>
            <span className="text-xl font-black font-mono" style={{ color: timerColor, transition: "color 0.7s" }}>{mm}:{ss}</span>
          </div>
          <button data-testid="exit-intent-buy-btn" onClick={buy} className="w-full py-3.5 bg-[#FF4F00] text-white rounded-2xl font-black text-sm flex items-center justify-center gap-2 hover:bg-orange-600 transition-all active:scale-[0.98] shadow-lg shadow-orange-500/25">
            <Gift size={16} /> ¡Activar mi sitio con dominio gratis!
          </button>
          <button onClick={close} className="w-full py-2 text-[11px] text-gray-400 hover:text-gray-600 transition-colors">
            No gracias, prefiero perder esta oportunidad
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Lead Capture Modal ───────────────────────────────────────────────────────
const COMPANY_SIZES = [
  "1 persona (autónomo)",
  "2 a 5 personas",
  "6 a 20 personas",
  "Más de 20 personas",
];

function LeadCaptureModal({ slug, onClose, onSuccess }) {
  const [form, setForm] = useState({ name: "", email: "", whatsapp: "", company_size: "", description: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [visible, setVisible] = useState(false);

  useEffect(() => { setTimeout(() => setVisible(true), 40); }, []);

  const close = () => { setVisible(false); setTimeout(onClose, 280); };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const { name, email, whatsapp, company_size } = form;
    if (!name || !email || !whatsapp || !company_size) {
      setError("Completá todos los campos obligatorios.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/lead-capture`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slug, ...form }),
      });
      const d = await res.json();
      if (d.success && d.redirect_url) {
        onSuccess(d.redirect_url);
      } else {
        setError("No se pudo procesar. Intentá nuevamente.");
      }
    } catch {
      setError("Error de conexión. Intentá más tarde.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center px-0 sm:px-4"
      style={{ background: "rgba(0,0,0,0.72)", backdropFilter: "blur(4px)" }}>
      <div
        data-testid="lead-capture-modal"
        className="bg-white w-full sm:max-w-sm sm:rounded-3xl rounded-t-3xl overflow-hidden shadow-2xl"
        style={{
          transform: visible ? "translateY(0) scale(1)" : "translateY(40px) scale(0.97)",
          opacity: visible ? 1 : 0,
          transition: "all 0.28s cubic-bezier(0.34,1.56,0.64,1)",
        }}
      >
        {/* Header */}
        <div className="bg-gradient-to-br from-[#0A0A0A] to-[#1a1a2e] px-5 pt-5 pb-5 relative">
          <button onClick={close} className="absolute top-4 right-4 w-7 h-7 flex items-center justify-center rounded-full bg-white/10 text-gray-400 hover:text-white">
            <X size={14} />
          </button>
          <div className="bg-[#ffc700] text-black text-[10px] font-black px-2.5 py-1 rounded-full tracking-wide inline-block mb-3">
            ACCESO GRATUITO
          </div>
          <h2 className="text-lg font-black text-white leading-tight mb-1">
            Probá tu sitio con 10 ediciones IA gratis
          </h2>
          <p className="text-xs text-gray-400">Sin tarjeta de crédito. Solo completá el formulario.</p>
        </div>

        {/* Form */}
        <form onSubmit={submit} className="px-5 py-4 space-y-3">
          {/* Name */}
          <div className="flex items-center gap-2.5 border border-gray-200 rounded-xl px-3 py-2.5 focus-within:border-[#6D28D9]">
            <User size={14} className="text-gray-400 flex-shrink-0" />
            <input
              data-testid="lead-name-input"
              value={form.name}
              onChange={e => set("name", e.target.value)}
              placeholder="Tu nombre *"
              className="flex-1 text-sm outline-none bg-transparent text-gray-800 placeholder-gray-400"
            />
          </div>
          {/* Email */}
          <div className="flex items-center gap-2.5 border border-gray-200 rounded-xl px-3 py-2.5 focus-within:border-[#6D28D9]">
            <Mail size={14} className="text-gray-400 flex-shrink-0" />
            <input
              data-testid="lead-email-input"
              type="email"
              value={form.email}
              onChange={e => set("email", e.target.value)}
              placeholder="Tu email *"
              className="flex-1 text-sm outline-none bg-transparent text-gray-800 placeholder-gray-400"
            />
          </div>
          {/* WhatsApp */}
          <div className="flex items-center gap-2.5 border border-gray-200 rounded-xl px-3 py-2.5 focus-within:border-[#6D28D9]">
            <Phone size={14} className="text-gray-400 flex-shrink-0" />
            <input
              data-testid="lead-whatsapp-input"
              value={form.whatsapp}
              onChange={e => set("whatsapp", e.target.value)}
              placeholder="Tu WhatsApp (con código de país) *"
              className="flex-1 text-sm outline-none bg-transparent text-gray-800 placeholder-gray-400"
            />
          </div>
          {/* Company size */}
          <div className="flex items-center gap-2.5 border border-gray-200 rounded-xl px-3 py-2.5 focus-within:border-[#6D28D9]">
            <Building2 size={14} className="text-gray-400 flex-shrink-0" />
            <select
              data-testid="lead-company-size-select"
              value={form.company_size}
              onChange={e => set("company_size", e.target.value)}
              className="flex-1 text-sm outline-none bg-transparent text-gray-800 appearance-none"
            >
              <option value="">Tamaño del negocio *</option>
              {COMPANY_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <ChevronDown size={14} className="text-gray-400 flex-shrink-0" />
          </div>
          {/* Description (optional) */}
          <textarea
            data-testid="lead-description-input"
            value={form.description}
            onChange={e => set("description", e.target.value)}
            placeholder="¿A qué se dedica tu negocio? (opcional)"
            rows={2}
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#6D28D9] resize-none text-gray-800 placeholder-gray-400"
          />
          {error && <p className="text-xs text-red-600 font-medium">{error}</p>}
          <button
            data-testid="lead-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 py-3.5 bg-[#0A0A0A] text-white rounded-2xl font-black text-sm hover:bg-gray-800 transition-all disabled:opacity-60 active:scale-[0.98]"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : null}
            {loading ? "Creando tu acceso..." : "Acceder a mi panel gratis →"}
          </button>
          <p className="text-center text-[10px] text-gray-400">También te enviamos el link por email para acceder después</p>
        </form>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function SitePreview() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [siteInfo, setSiteInfo] = useState(null);
  const [error, setError]       = useState(false);
  const [showWhatsApp, setShowWhatsApp] = useState(false);
  const [waForm, setWaForm] = useState({ name: "", consulta: "" });
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [showExitIntent, setShowExitIntent]   = useState(false);
  const [showLeadCapture, setShowLeadCapture] = useState(false);
  const exitShownRef = useRef(false);

  const tier = useTier(siteInfo);
  const waLabel = useWaLabel(tier);

  useEffect(() => {
    const isAdminPreview = new URLSearchParams(window.location.search).get("admin_preview") === "true";
    const url = isAdminPreview ? `${API}/sites/${slug}?admin_preview=true` : `${API}/sites/${slug}`;
    fetch(url)
      .then(r => r.json())
      .then(data => { if (data.detail) setError(true); else setSiteInfo(data); })
      .catch(() => setError(true));
  }, [slug]);

  // Exit intent
  useEffect(() => {
    if (!siteInfo) return;
    const handleLeave = (e) => {
      if (e.clientY <= 5 && !exitShownRef.current) { exitShownRef.current = true; setShowExitIntent(true); }
    };
    let lastY = window.scrollY, lastT = Date.now();
    const handleScroll = () => {
      const now = Date.now(), delta = lastY - window.scrollY, vel = delta / (now - lastT);
      if (vel > 3 && !exitShownRef.current) { exitShownRef.current = true; setShowExitIntent(true); }
      lastY = window.scrollY; lastT = now;
    };
    document.addEventListener("mouseleave", handleLeave);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => { document.removeEventListener("mouseleave", handleLeave); window.removeEventListener("scroll", handleScroll); };
  }, [siteInfo]);

  const handleAcquire = async () => {
    // Tier 3 (expired): redirect to sitiosya.com.uy
    if (tier?.level === 3) { window.open(SITIOSYA_URL, "_blank"); return; }
    // Tier 1 & 2: show lead capture form instead of immediate Stripe checkout
    setShowLeadCapture(true);
  };

  const handleLeadSuccess = (redirectUrl) => {
    navigate(redirectUrl);
  };

  const handleWa = () => {
    const { name, consulta } = waForm;
    const text = `Hola! Consulta sobre mi sitio web.%0A%0ANombre: ${encodeURIComponent(name)}%0AConsulta: ${encodeURIComponent(consulta)}`;
    window.open(`https://wa.me/${siteInfo?.whatsapp_number || "59897401287"}?text=${text}`, "_blank");
    setShowWhatsApp(false);
  };

  if (error) return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="text-center p-8">
        <h2 className="text-xl font-bold text-gray-800 mb-2">Sitio no encontrado</h2>
        <p className="text-gray-500">La URL de vista previa no es válida o ha expirado.</p>
      </div>
    </div>
  );

  const isPaid = siteInfo?.is_paid || false;

  // When paid: show ONLY the site, no promotional elements at all
  if (isPaid) {
    return (
      <iframe
        data-testid="site-preview-iframe"
        src={`${process.env.REACT_APP_BACKEND_URL}/api/sites/${slug}/html`}
        title="Mi sitio web"
        style={{ position: "fixed", inset: 0, width: "100%", height: "100%", border: "none" }}
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-modals"
      />
    );
  }

  const priceLabel   = tier ? formatPrice(tier.price, tier.currency) : "cargando...";
  const fullLabel    = tier?.fullPrice ? formatPrice(tier.fullPrice, tier.currency) : null;
  const businessName = siteInfo?.business_name || "";

  return (
    <div className="fixed inset-0 flex flex-col bg-gray-100">

      <style>{`
        @keyframes bubbleEnter { from { transform:translateX(-40px) scale(0.85); opacity:0; } to { transform:translateX(0) scale(1); opacity:1; } }
        .bubble-enter { animation: bubbleEnter 0.4s cubic-bezier(0.34,1.56,0.64,1) forwards; }
        @keyframes waRipple { 0%{box-shadow:0 0 0 0 rgba(37,211,102,.5)} 70%{box-shadow:0 0 0 14px rgba(37,211,102,0)} 100%{box-shadow:0 0 0 0 rgba(37,211,102,0)} }
      `}</style>

      {/* Top Bar secundario — nombre del negocio */}
      <div className="bg-white border-b border-gray-200 flex-shrink-0">
        <div className="px-4 py-1.5 flex items-center justify-between">
          <span className="text-xs text-gray-500 truncate max-w-[60%]">{businessName || "Vista Previa"}</span>
          {isPaid ? (
            <span className="text-xs font-bold text-green-700 bg-green-100 px-2.5 py-0.5 rounded-full flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" /> Activo
            </span>
          ) : (
            <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">DEMO</span>
          )}
        </div>
      </div>

      {/* iframe */}
      <iframe data-testid="site-preview-iframe" src={`${process.env.REACT_APP_BACKEND_URL}/api/sites/${slug}/html`} title="Vista previa del sitio" className="flex-1 w-full border-none" sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-modals" />

      {/* WhatsApp FAB — hidden when paid */}
      {!isPaid && (
        <div className="fixed right-4 z-40 flex flex-col items-center gap-1.5" style={{ bottom: "165px" }}>
          <div
            key={waLabel}
            className="bg-[#0A0A0A] text-white text-[11px] font-semibold px-2.5 py-1 rounded-full shadow-md whitespace-nowrap"
            style={{ animation: "bubbleEnter 0.4s cubic-bezier(0.34,1.56,0.64,1) forwards" }}
          >
            {waLabel}
          </div>
          <button data-testid="whatsapp-fab" onClick={() => setShowWhatsApp(true)} className="w-12 h-12 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow-lg" style={{ animation: "waRipple 2s infinite" }}>
            <MessageCircle size={22} strokeWidth={1.5} />
          </button>
        </div>
      )}

      {/* Bottom CTA Bar — hidden when paid */}
      {!isPaid && (
        <div className="fixed bottom-0 left-0 right-0 z-30 flex justify-center pb-2 px-3 pointer-events-none">
          <div
            data-testid="cta-bar"
            className="w-full max-w-[430px] flex flex-col gap-2 px-4 py-3 rounded-2xl pointer-events-auto"
            style={{ background: "rgba(255,255,255,0.97)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", boxShadow: "0 -2px 40px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.06)" }}
          >
            {/* Tier 1: "Oferta de única vez" */}
            {tier?.level === 1 && (
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-black bg-[#FF4F00] text-white px-2 py-0.5 rounded-md tracking-wide flex-shrink-0">50% OFF</span>
                  <span className="text-base font-black text-[#FF4F00] leading-tight">Oferta de única vez</span>
                </div>
              </div>
            )}

            {/* Tier 3: expired notice */}
            {tier?.level === 3 && (
              <div className="flex items-center justify-center gap-1.5">
                <span className="text-[11px] text-gray-500">Oferta expirada · Solo disponible en</span>
                <a href={SITIOSYA_URL} target="_blank" rel="noreferrer" className="text-[11px] font-bold text-[#0074b7] flex items-center gap-0.5">sitiosya.com.uy <ExternalLink size={10} /></a>
              </div>
            )}

            {/* Countdown — sin repetir el descuento, solo si está activo */}
            {tier && tier.level < 3 && (
              <div className="flex items-center justify-center gap-2 flex-wrap">
                <Clock size={13} className="text-[#FF4F00] flex-shrink-0" />
                <span className="text-xs text-gray-600 font-semibold">Termina en:</span>
                <div className="flex items-center gap-1">
                  <CountdownUnit value={tier.countdown.h} />
                  <span className="text-[#FF4F00] font-bold text-sm">:</span>
                  <CountdownUnit value={tier.countdown.m} />
                  <span className="text-[#FF4F00] font-bold text-sm">:</span>
                  <CountdownUnit value={tier.countdown.s} />
                </div>
              </div>
            )}

            {/* CTA Button */}
            {tier?.level === 3 ? (
              <a href={SITIOSYA_URL} target="_blank" rel="noreferrer" data-testid="purchase-website-btn"
                className="w-full flex items-center justify-center gap-2 py-3.5 bg-[#0074b7] text-white rounded-2xl font-bold text-base hover:bg-blue-700 transition-colors active:scale-[0.98]">
                <ExternalLink size={18} /> Ver precio en sitiosya.com.uy
              </a>
            ) : (
              <button data-testid="purchase-website-btn" onClick={handleAcquire} disabled={checkoutLoading}
                className="w-full flex items-center justify-center gap-2 py-3 bg-[#FF4F00] text-white rounded-2xl font-bold hover:bg-orange-600 transition-colors disabled:opacity-60 active:scale-[0.98]"
              >
                {checkoutLoading ? (
                  <span>Procesando...</span>
                ) : tier?.level === 1 ? (
                  <div className="flex items-center gap-2.5">
                    <ShoppingCart size={17} strokeWidth={2} className="flex-shrink-0" />
                    <div className="flex flex-col items-start leading-tight">
                      <span className="text-sm font-bold">Activar ahora con 50% OFF</span>
                      <div className="flex items-center gap-1.5">
                        {fullLabel && <span className="line-through opacity-55 text-xs font-normal">{fullLabel}</span>}
                        <span className="text-sm font-black">{priceLabel}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <ShoppingCart size={17} strokeWidth={2} className="flex-shrink-0" />
                    <span className="text-sm font-bold">Adquirir mi sitio — {priceLabel}</span>
                  </div>
                )}
              </button>
            )}
          </div>
        </div>
      )}

      {/* WhatsApp Modal — only when not paid */}
      {!isPaid && showWhatsApp && (
        <div data-testid="whatsapp-modal" className="fixed inset-0 z-50 flex items-end justify-center" style={{ background: "rgba(0,0,0,0.4)" }} onClick={e => e.target === e.currentTarget && setShowWhatsApp(false)}>
          <div className="bg-white w-full max-w-md rounded-t-3xl p-6 pb-8 animate-fadeInUp">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-[#25D366] rounded-full flex items-center justify-center"><MessageCircle size={16} className="text-white" /></div>
                <div><h3 className="text-base font-bold text-[#0A0A0A]">Consultar por WhatsApp</h3><p className="text-xs text-gray-400">Te responderemos a la brevedad</p></div>
              </div>
              <button onClick={() => setShowWhatsApp(false)} className="p-1.5 hover:bg-gray-100 rounded-lg"><X size={18} className="text-gray-500" /></button>
            </div>
            <div className="space-y-3">
              <FormField label="Tu nombre" value={waForm.name} onChange={v => setWaForm(f => ({ ...f, name: v }))} placeholder="¿Cómo te llamás?" testId="wa-name-input" />
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Tu consulta</label>
                <textarea
                  data-testid="wa-consulta-input"
                  value={waForm.consulta}
                  onChange={e => setWaForm(f => ({ ...f, consulta: e.target.value }))}
                  placeholder="¿Qué querés saber?"
                  rows={3}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-gray-400 resize-none"
                />
              </div>
              <button
                data-testid="wa-submit-btn"
                onClick={handleWa}
                disabled={!waForm.name || !waForm.consulta}
                className="w-full flex items-center justify-center gap-2 py-3.5 bg-[#25D366] text-white rounded-2xl font-bold text-sm hover:bg-green-600 transition-colors disabled:opacity-50"
              >
                <Send size={16} /> Abrir WhatsApp
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Exit Intent — only when not paid */}
      {!isPaid && showExitIntent && (
        <ExitIntentModal onBuy={() => { setShowExitIntent(false); handleAcquire(); }} onClose={() => setShowExitIntent(false)} />
      )}

      {/* Lead Capture Modal */}
      {showLeadCapture && (
        <LeadCaptureModal
          slug={slug}
          onClose={() => setShowLeadCapture(false)}
          onSuccess={handleLeadSuccess}
        />
      )}
    </div>
  );
}

function CountdownUnit({ value }) {
  return (
    <span
      className="font-black text-sm font-heading min-w-[28px] text-center text-[#111] bg-[#fff3ea] border border-[#ffdcc4] rounded-md px-1.5 py-0.5 tabular-nums"
    >
      {String(value).padStart(2, "0")}
    </span>
  );
}
function FormField({ label, value, onChange, placeholder, testId }) {
  return (
    <div>
      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">{label}</label>
      <input data-testid={testId} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-gray-400" />
    </div>
  );
}
